import Backbone from 'backbone'
import _ from 'lodash'
import XhrStream from 'buffered-xhr-stream'
import Parser from 'jsonparse'
import Chunker from 'scoreboard/components/chunker'
import through from 'through'
import errorHandler from 'scoreboard/components/error-handler'
import Xhr from 'scoreboard/components/xhr'
import constants from 'scoreboard/components/constants'

/**
 * The JSONStream module can handle an array of data (i.e. [{ id: 1 },...] )
 *
 * This module allows you to send some metadata before your stream, like this:
 * { metadata: {}, stream: [] }
 *
 * @emits JsonStreamer#stream
 */
function JsonStreamer() { }

_.extend(JsonStreamer.prototype, Backbone.Events, {

	/**
	 * Start streaming with the given options, which are:
	 *
	 * @param {String} options.url the URL to stream, e.g. api/home/my-metrics
	 * @param {String} options.stream the name of the property to stream, e.g. 'metrics'
	 * @param {Function} [options.error=displayErrorHandler] will be called if error occurs,
	 * default is to invoke display-error-handler.js and set the display view based on response code.
	 * @param {String} [options.method=GET] HTTP method to use (e.g. GET, POST)
	 * @param {String} [options.data] JSON String passed to xhr.send()
	 * @param {String} [options.contentType='application/json']
	 */
	start: function(options = {}) {
		// Use Xhr so CSRF token is included
		this._xhr = new Xhr(options)
		// start streaming ASAP
		this.xhrStream = new XhrStream({
			xhr: this._xhr,
			contentType: _.get(options, 'contentType', constants.CONTENT_TYPES.APPLICATION_JSON),
			stream: options.stream,
			data: options.data
		})

		// buffered-xhr-stream emits an error object with the `xhr` it used.
		// In this instance it is the same as this._xhr since we provide it
		// during initialization
		this.xhrStream.on('error', e => {
			options.error ? options.error(e) : errorHandler(e.xhr)
			this.trigger('error', e)
		})

		// setup through stream
		let backPressureBuffer = []
		var t = through(
			function (chunk) {
				if('string' === typeof chunk) {
					chunk = new Buffer(chunk)
				}
				//#25175 don't interrupt the parser with more data until it's
				//finished parsing the latest chunk
				if (this._parsing) {
					//apply backpressure
					this.pause()
					backPressureBuffer.push(chunk)
				} else {
					this._parsing = true
					parser.write(chunk)
					//work off the stuff that came in before backpressure applied
					if (backPressureBuffer.length) {
						for (let b; b = backPressureBuffer.shift(); ) {
							parser.write(b)
						}
						this._parsing = false
						this.resume()
					} else {
						this._parsing = false
					}
				}
			},
			function (data) {
				if(data)
					t.write(data)
				// DON'T finish the stream yet!  let the parser do that once
				// it has had a chance to process everything
			}
		)

		// setup parser
		var parser = new Parser()
		parser.onValue = function(value) {
			if (!parser.stack) {
				return
			}
			// handle top-level fields that shouldn't be streamed
			if (parser.stack.length == 1) {
				this.trigger('data', parser.key, value)
			}
			// handle the stream
			if (parser.stack.length == 2 && parser.stack[1].key === options.stream) {
				t.queue(value)
			}
			// handle end of the stream
			if (!parser.mode) {
				t.queue(null)
			}
		}.bind(this)

		// tie it all together
		var chunker = new Chunker(this.xhrStream, options)
		/**
		 * Stream event
		 * @event JsonStreamer#stream
		 * @type {object} the stream piped through the chunker
		 */
		this.trigger('stream', this.xhrStream.pipe(t).pipe(chunker))
	},

	/**
	 * Remove all event listeners and abort the XHR if it's still streaming data
	 * from the server (can't be cancelled on server, but we can stop receiving
	 * on the client).  Necessary when navigating away from a view still
	 * streaming data (#26166)
	 */
	abort: function() {
		if (this._xhr.readyState !== XMLHttpRequest.DONE) {
			_.invoke(this.xhrStream, 'removeAllListeners')
			_.invoke(this._xhr, 'abort')
		}
	}

})

/**
 * @function StreamFunction
 * @param {Chunker} stream emitted from JsonStreamer.on('stream')
 */

/**
 * Centralizes the concerns related to instantiating and starting
 * a JsonStreamer that is consumed by a view.
 *
 * @param {Backbone.View} view The view which consumes the stream, requires specific functions to be defined:
 * @param {StreamFunction} view.setupStream The view should setup listeners on this Chunker stream
 * @param {Function} view.set<Key> where <Key> is the key emitted from streamer.on('data').  This corresponds
 * to the metadata properties in the response before the 'stream' property.
 *
 * @param {Object} streamerOptions see JsonStreamer#start
 *
 * @param {Function} options.dataFn A function that will be invoked with (key,value)
 * Allows callers to do additional work with each data attribute streamed
 *
 * @return {JsonStreamer} the streamer, can be used to perform additional actions
 * unrelated to the data stream (e.g. read XHR headers)
 */
function initiate(view, streamerOptions, options = {}) {
	var streamer = new JsonStreamer()
	view.on('remove', () => streamer.abort())
	view.listenTo(streamer, 'stream', chunkerStream => {
		view.setupStream(chunkerStream)
	})
	view.listenTo(streamer, 'data', (key, value) => {
		_.invoke(view, _.camelCase('set '+ key), value)
		_.invoke(options, 'dataFn', key, value)
	})
	streamer.start(streamerOptions)
	return streamer
}

JsonStreamer.initiate = initiate

export default JsonStreamer
JsonStreamer.__filename__ = 'components/json-streamer.js'
